import type { AttributionInput } from "../integrations/api";

const ATTRIBUTION_STORAGE_KEY = "conan.quiz.attribution.v1";
const DEFAULT_RETENTION_MS = 90 * 24 * 60 * 60 * 1_000;

const QUERY_KEYS = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
  site_source_name: "siteSourceName",
  campaign_id: "campaignId",
  adset_id: "adsetId",
  ad_id: "adId",
  placement: "placement",
  fbclid: "fbclid",
  gclid: "gclid",
} as const;
const ATTRIBUTION_QUERY_KEYS = new Set<string>(Object.keys(QUERY_KEYS));

type QueryKey = keyof typeof QUERY_KEYS;
type AttributionKey = (typeof QUERY_KEYS)[QueryKey];

export interface AttributionRecord extends AttributionInput {
  capturedAt: string;
  expiresAt: string;
}

export interface AttributionCaptureOptions {
  url?: string;
  referrer?: string;
  includeMarketingCookies?: boolean;
  storage?: Storage;
  cookie?: string;
  now?: Date;
  retentionMs?: number;
}

function availableStorage(provided?: Storage): Storage | undefined {
  if (provided) return provided;
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function clean(value: string | null | undefined, maximumLength: number): string | undefined {
  if (!value) return undefined;
  const printable = Array.from(value, (character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 ? " " : character;
  }).join("");
  const normalized = printable.trim().replace(/\s+/g, " ");
  if (!normalized) return undefined;
  return normalized.slice(0, maximumLength);
}

function safeHttpUrl(value: string | undefined): string | undefined {
  const cleaned = clean(value, 1_000);
  if (!cleaned) return undefined;
  try {
    const url = new URL(cleaned);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    url.username = "";
    url.password = "";
    return url.toString().slice(0, 1_000);
  } catch {
    return undefined;
  }
}

function safeLandingUrl(
  value: string | undefined,
  includeMarketingIdentifiers = true,
): string | undefined {
  const safe = safeHttpUrl(value);
  if (!safe) return undefined;
  const url = new URL(safe);
  for (const key of Array.from(url.searchParams.keys())) {
    if (
      !ATTRIBUTION_QUERY_KEYS.has(key)
      || (!includeMarketingIdentifiers && (key === "fbclid" || key === "gclid"))
    ) url.searchParams.delete(key);
  }
  url.hash = "";
  return url.toString().slice(0, 1_000);
}

function safeReferrerUrl(value: string | undefined): string | undefined {
  const safe = safeHttpUrl(value);
  if (!safe) return undefined;
  const url = new URL(safe);
  url.search = "";
  url.hash = "";
  return url.toString().slice(0, 1_000);
}

function cookieValue(cookieHeader: string, name: "_fbp" | "_fbc"): string | undefined {
  for (const item of cookieHeader.split(";")) {
    const separator = item.indexOf("=");
    if (separator < 0) continue;
    if (item.slice(0, separator).trim() !== name) continue;
    try {
      return clean(decodeURIComponent(item.slice(separator + 1).trim()), 255);
    } catch {
      return clean(item.slice(separator + 1).trim(), 255);
    }
  }
  return undefined;
}

function currentHref(): string | undefined {
  return typeof window === "undefined" ? undefined : window.location.href;
}

function currentReferrer(): string | undefined {
  return typeof document === "undefined" ? undefined : document.referrer;
}

function currentCookie(): string {
  return typeof document === "undefined" ? "" : document.cookie;
}

function readStored(storage: Storage | undefined, now: Date): AttributionRecord | undefined {
  if (!storage) return undefined;
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return undefined;
    const candidate = parsed as Record<string, unknown>;
    if (typeof candidate.capturedAt !== "string" || typeof candidate.expiresAt !== "string") return undefined;
    if (Number.isNaN(Date.parse(candidate.expiresAt)) || Date.parse(candidate.expiresAt) <= now.getTime()) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return undefined;
    }
    return sanitizeRecord(candidate, candidate.capturedAt, candidate.expiresAt);
  } catch {
    return undefined;
  }
}

function sanitizeRecord(
  candidate: Record<string, unknown>,
  capturedAt: string,
  expiresAt: string,
): AttributionRecord {
  const output: AttributionRecord = { capturedAt, expiresAt };
  const landingUrl = safeLandingUrl(typeof candidate.landingUrl === "string" ? candidate.landingUrl : undefined);
  const referrer = safeReferrerUrl(typeof candidate.referrer === "string" ? candidate.referrer : undefined);
  if (landingUrl) output.landingUrl = landingUrl;
  if (referrer) output.referrer = referrer;
  const maximumLengths: Record<AttributionKey | "fbp" | "fbc", number> = {
    utmSource: 120,
    utmMedium: 120,
    utmCampaign: 180,
    utmContent: 180,
    utmTerm: 180,
    siteSourceName: 80,
    campaignId: 128,
    adsetId: 128,
    adId: 128,
    placement: 120,
    fbclid: 255,
    gclid: 255,
    fbp: 255,
    fbc: 255,
  };
  for (const [key, maximumLength] of Object.entries(maximumLengths)) {
    const value = clean(typeof candidate[key] === "string" ? candidate[key] : undefined, maximumLength);
    if (value) (output as unknown as Record<string, unknown>)[key] = value;
  }
  return output;
}

function withoutMarketingIdentifiers(record: AttributionRecord): AttributionRecord {
  const safe = { ...record };
  delete safe.fbclid;
  delete safe.gclid;
  delete safe.fbp;
  delete safe.fbc;
  const landingUrl = safeLandingUrl(safe.landingUrl, false);
  if (landingUrl) safe.landingUrl = landingUrl;
  else delete safe.landingUrl;
  return safe;
}

function toAttributionInput(record: AttributionRecord): AttributionInput {
  const attribution: Partial<AttributionRecord> = { ...record };
  delete attribution.capturedAt;
  delete attribution.expiresAt;
  return attribution;
}

export function captureAttribution(options: AttributionCaptureOptions = {}): AttributionInput {
  const now = options.now ?? new Date();
  const retentionMs = options.retentionMs ?? DEFAULT_RETENTION_MS;
  const storage = availableStorage(options.storage);
  const previous = readStored(storage, now);
  const href = options.url ?? currentHref();
  const referrer = options.referrer ?? currentReferrer();
  const current: Partial<AttributionRecord> = {};
  let hasCurrentCampaignSignal = false;

  if (href) {
    const landingUrl = safeLandingUrl(href, options.includeMarketingCookies === true);
    if (landingUrl) {
      current.landingUrl = landingUrl;
      const url = new URL(landingUrl);
      for (const [queryKey, recordKey] of Object.entries(QUERY_KEYS) as Array<[QueryKey, AttributionKey]>) {
        if (!options.includeMarketingCookies && (queryKey === "fbclid" || queryKey === "gclid")) continue;
        const maximumLength = queryKey.startsWith("utm_") ? 180 : 255;
        const value = clean(url.searchParams.get(queryKey), maximumLength);
        if (value) {
          current[recordKey] = value;
          hasCurrentCampaignSignal = true;
        }
      }
    }
  }
  const safeReferrer = safeReferrerUrl(referrer);
  if (safeReferrer) current.referrer = safeReferrer;

  // Keep the most recent non-direct campaign. Internal/direct navigation must
  // not replace the landing URL that actually brought the visitor into the
  // funnel, while a fresh paid click should supersede an older campaign.
  if (previous && !hasCurrentCampaignSignal) {
    delete current.landingUrl;
    delete current.referrer;
  }

  if (options.includeMarketingCookies) {
    const cookie = options.cookie ?? currentCookie();
    const fbp = cookieValue(cookie, "_fbp");
    const storedFbc = cookieValue(cookie, "_fbc");
    if (fbp) current.fbp = fbp;
    if (storedFbc) current.fbc = storedFbc;
    else if (current.fbclid) current.fbc = `fb.1.${now.getTime()}.${current.fbclid}`.slice(0, 255);
  }

  const capturedAt = hasCurrentCampaignSignal || !previous ? now.toISOString() : previous.capturedAt;
  const expiresAt = hasCurrentCampaignSignal || !previous
    ? new Date(now.getTime() + retentionMs).toISOString()
    : previous.expiresAt;
  const merged = sanitizeRecord(
    { ...(hasCurrentCampaignSignal ? {} : previous), ...current },
    capturedAt,
    expiresAt,
  );

  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Attribution still travels with the current session when persistent storage is unavailable.
  }

  const visible = options.includeMarketingCookies ? merged : withoutMarketingIdentifiers(merged);
  return toAttributionInput(visible);
}

export function getStoredAttribution(options: {
  includeMarketingCookies?: boolean;
  storage?: Storage;
  now?: Date;
} = {}): AttributionInput {
  const record = readStored(availableStorage(options.storage), options.now ?? new Date());
  if (!record) return {};
  const visible = options.includeMarketingCookies ? record : withoutMarketingIdentifiers(record);
  return toAttributionInput(visible);
}

export function clearMarketingAttribution(storage?: Storage): void {
  const target = availableStorage(storage);
  const record = readStored(target, new Date());
  if (record) {
    try {
      target?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(withoutMarketingIdentifiers(record)));
    } catch {
      // Revocation remains effective because reads omit marketing cookies by default.
    }
  }
  if (typeof document !== "undefined") {
    document.cookie = "_fbp=; Max-Age=0; Path=/; SameSite=Lax";
    document.cookie = "_fbc=; Max-Age=0; Path=/; SameSite=Lax";
  }
}

export function clearAttribution(storage?: Storage): void {
  try {
    availableStorage(storage)?.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy modes.
  }
}

export function withAttribution(target: string, attribution: AttributionInput = getStoredAttribution()): string {
  const base = currentHref() ?? "http://localhost/";
  const absoluteInput = /^[a-z][a-z\d+.-]*:/i.test(target);
  const url = new URL(target, base);
  const values: Array<[QueryKey, string | undefined]> = [
    ["utm_source", attribution.utmSource],
    ["utm_medium", attribution.utmMedium],
    ["utm_campaign", attribution.utmCampaign],
    ["utm_content", attribution.utmContent],
    ["utm_term", attribution.utmTerm],
    ["fbclid", attribution.fbclid],
    ["gclid", attribution.gclid],
  ];
  for (const [key, value] of values) {
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  if (absoluteInput) return url.toString();
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Carries only known campaign parameters across an in-app navigation without
 * persisting or exposing them to trackers. This keeps click IDs available if
 * the visitor grants marketing consent on the next route.
 */
export function withCurrentAttributionQuery(
  target: string,
  sourceUrl: string | undefined = currentHref(),
): string {
  if (!sourceUrl) return target;
  try {
    const base = currentHref() ?? "http://localhost/";
    const destination = new URL(target, base);
    const source = new URL(sourceUrl, base);
    for (const key of ATTRIBUTION_QUERY_KEYS) {
      const value = clean(source.searchParams.get(key), key.startsWith("utm_") ? 180 : 255);
      if (value && !destination.searchParams.has(key)) destination.searchParams.set(key, value);
    }
    return `${destination.pathname}${destination.search}${destination.hash}`;
  } catch {
    return target;
  }
}
